import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { CiSearch } from "react-icons/ci";
import { IoMdArrowDropdown } from "react-icons/io";

interface DropdownItem {
  address: string;
  name: string;
  avatar?: string;
  indexInCollection?: number;
}

interface DropdownSearchInputProps {
  className?: string;
  placeholder?: string;
  items: DropdownItem[];
  onSelect: (item: DropdownItem) => void;
  isLoading?: boolean;
}

export default function DropdownSearchInput({
  className,
  placeholder,
  items,
  onSelect,
  isLoading = false,
}: DropdownSearchInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleItemSelect = (item: DropdownItem) => {
    onSelect(item);
    setSearchValue("");
    setIsOpen(false);
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="flex gap-[11px] pt-[7px] pb-[7px] pl-[12px] pr-[12px] border-[#4E4E4E] border-[2px] focus-within:border-[#333333] transition-all duration-300">
        <CiSearch className="text-[#4E4E4E] w-[18px] h-[18px]" />
        <input
          type="text"
          placeholder={placeholder}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onFocus={() => setIsOpen(true)}
          className="w-full text-[#4E4E4E] placeholder-[#4E4E4E] text-[12px] bg-transparent outline-none font-archivo"
          disabled={isLoading}
        />
        <button
          onClick={handleToggleDropdown}
          className="hover:opacity-70 transition-opacity"
          disabled={isLoading}
        >
          <IoMdArrowDropdown
            className={`text-[#4E4E4E] w-[20px] h-[20px] transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-[#1a1a1a] border-[2px] border-[#4E4E4E] max-h-[200px] overflow-y-auto">
          {isLoading ? (
            <div className="p-3 text-center text-[#4E4E4E] text-[12px]">
              Loading...
            </div>
          ) : filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <div
                key={`${item.address}-${index}`}
                onClick={() => handleItemSelect(item)}
                className="flex items-center gap-2 p-2 hover:bg-[#2B2B2B] cursor-pointer transition-colors"
              >
                {item.avatar && (
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className="w-8 h-8 object-cover rounded"
                  />
                )}
                <div className="flex flex-col flex-1">
                  <span className="text-white text-[12px] font-medium truncate">
                    {item.name}
                  </span>
                  {item.indexInCollection !== undefined && (
                    <span className="text-[#4E4E4E] text-[10px]">
                      #{item.indexInCollection}
                    </span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-center text-[#4E4E4E] text-[12px]">
              No items found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
